// Lexicographic midpoint rank generator. Same idea Trello/Jira use:
// store ranks as strings, drop a card "between" two neighbors by finding
// any string that sorts strictly between them. Alphabet is 'a'..'z'.
// New columns start with 'm' (mid-alphabet) so we can always extend
// before or after without collisions.
const FIRST_CHAR = 'a';
const LAST_CHAR = 'z';
const MID_CHAR = 'm';
export const START_RANK = MID_CHAR; // first item in an empty column
function midChar(a, b) {
    // a and b are single chars in [a-z]; returns the single char midpoint, or '' if they're adjacent.
    const ac = a.charCodeAt(0);
    const bc = b.charCodeAt(0);
    if (bc - ac < 2)
        return '';
    return String.fromCharCode(Math.floor((ac + bc) / 2));
}
/**
 * Return a rank string strictly between `before` and `after`.
 *  - both null → START_RANK (first item in an empty column)
 *  - before null, after set → a rank lexicographically less than `after`
 *  - before set, after null → a rank lexicographically greater than `before`
 *  - both set → a rank strictly between
 *
 * Throws if `before >= after`.
 */
export function rankBetween(before, after) {
    if (before === null && after === null)
        return START_RANK;
    if (before === null) {
        // Prepend before `after`. Walk left from `after`, lowering chars until we have room.
        const target = after;
        let prefix = '';
        for (let i = 0; i < target.length; i++) {
            const c = target.charAt(i);
            if (c > FIRST_CHAR) {
                const lower = String.fromCharCode(c.charCodeAt(0) - 1);
                return prefix + lower;
            }
            // c === 'a' — keep `a`, descend further.
            prefix += c;
        }
        // target was all 'a' chars, e.g. "a" or "aaa"; extend with 'm'.
        // But we need a rank LESS than target, so any extension makes it longer and equal-prefix-wise
        // greater. That can't happen — flag as error.
        throw new Error(`cannot prepend before ${target}: target is at floor`);
    }
    if (after === null) {
        // Append after `before`. Walk left from `before`, raising chars until we have room.
        const target = before;
        let prefix = '';
        for (let i = 0; i < target.length; i++) {
            const c = target.charAt(i);
            if (c < LAST_CHAR) {
                const higher = String.fromCharCode(c.charCodeAt(0) + 1);
                return prefix + higher;
            }
            prefix += c;
        }
        // All 'z' — extend with 'm' to grow strictly greater.
        return target + MID_CHAR;
    }
    if (before >= after) {
        throw new Error(`rankBetween: before (${before}) must be < after (${after})`);
    }
    // Both set. Walk char by char.
    const maxLen = Math.max(before.length, after.length);
    let prefix = '';
    for (let i = 0; i < maxLen; i++) {
        const a = before.charAt(i) || FIRST_CHAR;
        const b = after.charAt(i) || LAST_CHAR;
        if (a === b) {
            prefix += a;
            continue;
        }
        const mid = midChar(a, b);
        if (mid !== '')
            return prefix + mid;
        // Adjacent chars (e.g. 'a','b'). Take `a`, then need to insert AFTER `before`'s remaining suffix.
        // Use before.slice(i+1) — the portion after the current char — as the new lower bound.
        // This produces e.g. rankBetween('a','b') → 'a' + rankBetween(null, null) = 'am', which satisfies 'a' < 'am' < 'b'.
        const beforeRest = before.slice(i + 1) || null;
        return prefix + a + rankBetween(beforeRest, null);
    }
    // The both-set loop only reaches this point when `before` is a strict prefix
    // of `after` AND every char of `after` past `before.length` is 'a' (any other
    // char would have caused divergence during the loop and been handled there).
    // For inputs of the form `(X, X + 'a'+ + 'a'+ ...)`, no valid rank exists in
    // a 26-letter alphabet: any r > X starting with X needs a next char < 'a',
    // which doesn't exist. Throw to make this explicit.
    throw new Error(`rankBetween: no valid rank exists strictly between '${before}' and '${after}'`);
}
